// src/pages/client/ClientJobReportPage.jsx
// Job report showing all candidates ranked by score - client version

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackPageView, trackReportViewed, trackAnalysisTriggered } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

export default function ClientJobReportPage({ tenantKeyFromSubdomain }) {
  const navigate = useNavigate();
  const { tenantKey: tenantKeyFromUrl, jobId } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  
  const [tenant, setTenant] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);

  // Generate correct paths based on routing mode
  const getPath = (path) => {
    if (isSubdomain) {
      return `/admin${path}`;
    } else {
      return `/client/${tenantKey}${path}`;
    }
  };

  // Track page view
  useEffect(() => {
    const pagePath = isSubdomain ? '/admin/jobs/[id]/report' : '/client/[key]/jobs/[id]/report';
    trackPageView(pagePath, 'Client Job Report');
  }, [isSubdomain]);

  useEffect(() => {
    fetchReport();
    loadTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, tenantKey]);

  // Track report viewed when report loads
  useEffect(() => {
    if (report && jobId) {
      trackReportViewed('job', jobId);
    }
  }, [report, jobId]);

  const loadTenant = async () => {
    if (!tenantKey) return;
    try {
      const response = await fetch(`${API_BASE}/tenants/${tenantKey}`);
      if (response.ok) {
        setTenant(await response.json());
      }
    } catch (err) {
      console.error("Failed to load tenant:", err);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await fetch(`${API_BASE}/scoring/job/${jobId}/report`);
      if (!response.ok) {
        throw new Error('Failed to load report');
      }
      const data = await response.json();
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const analyzeAll = async () => {
    setAnalyzingAll(true);
    
    // Track bulk analysis
    trackAnalysisTriggered(null, false);
    
    try {
      await fetch(`${API_BASE}/scoring/analyze-all/${jobId}`, {
        method: 'POST'
      });
      // Refresh report after analysis
      await fetchReport();
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setAnalyzingAll(false);
    }
  };

  // Apply tenant branding (computed early for use in all renders, memoized)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || "#3ECF8E";
  const companyName = branding.companyName || tenant?.name || report?.company_name || '';
  
  // Get backgroundColor from branding (matches ClientConfigPage approach)
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark for text color
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
    return false;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding (matching ClientConfigPage exactly)
  const dynamicStyles = useMemo(() => {
    const textColor = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const buttonBg = branding.buttonPrimaryBg || primaryColor;
    const buttonText = branding.buttonTextColor || '#ffffff';
    
    return {
      container: { minHeight: "100vh", backgroundColor: backgroundColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
      header: { padding: "32px 40px", borderBottom: `1px solid ${cardBorder}` },
      backLink: { background: "none", border: "none", color: textSecondary, cursor: "pointer", marginBottom: 16, display: "inline-block", fontSize: 14 },
      title: { fontSize: 28, fontWeight: 700, margin: "0 0 8px", color: primaryColor },
      subtitle: { color: textSecondary, margin: 0 },
      companyName: { color: textSecondary, margin: "4px 0 0" },
      content: { padding: "32px 40px", maxWidth: 1200, margin: "0 auto" },
      card: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      button: { padding: "12px 24px", background: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
      textColor, textSecondary, cardBg, cardBorder, buttonBg, buttonText, isLightBackground
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);

  const getScoreColor = (score) => {
    if (score >= 8) return '#22c55e';
    if (score >= 6) return '#3b82f6';
    if (score >= 4) return '#eab308';
    return '#ef4444';
  };

  const getRecommendationStyle = (rec) => {
    switch (rec) {
      case 'Strong Hire':
        return { background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', border: '1px solid #22c55e' };
      case 'Hire':
        return { background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', border: '1px solid #3b82f6' };
      case 'Maybe':
        return { background: 'rgba(234, 179, 8, 0.2)', color: '#eab308', border: '1px solid #eab308' };
      case 'No Hire':
        return { background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: '1px solid #ef4444' };
      default:
        return { background: 'rgba(255,255,255,0.1)', color: '#9ca3af' };
    }
  };

  if (loading) {
    return (
      <div style={dynamicStyles.container}>
        <div style={styles.loading}>Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={dynamicStyles.container}>
        <div style={styles.error}>
          <h2 style={{ color: dynamicStyles.textColor }}>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(getPath('/reports'))} style={dynamicStyles.button}>
            ← Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const candidates = report?.candidates || [];
  const unanalyzedCount = (report?.total_candidates || 0) - (report?.analyzed_candidates || 0);

  // Calculate recommendation counts
  const strongHireCount = candidates.filter(c => c.recommendation === 'Strong Hire').length;
  const hireCount = candidates.filter(c => c.recommendation === 'Hire').length;
  const maybeCount = candidates.filter(c => c.recommendation === 'Maybe' || !c.recommendation).length;
  // eslint-disable-next-line no-unused-vars
  const noHireCount = candidates.filter(c => c.recommendation === 'No Hire').length;

  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <div style={dynamicStyles.header}>
        <button 
          onClick={() => navigate(getPath('/reports'))}
          style={dynamicStyles.backLink}
        >
          ← Back to Reports
        </button>
        <h1 style={dynamicStyles.title}>{report?.job_title || 'Job Report'}</h1>
        <p style={dynamicStyles.companyName}>{companyName}</p>
        <p style={dynamicStyles.subtitle}>
          {report?.analyzed_candidates || 0} of {report?.total_candidates || 0} candidates analyzed
        </p>
      </div>

      <div style={dynamicStyles.content}>
        {/* Summary Stats */}
        <div style={styles.summaryGrid}>
          <div style={{ ...styles.summaryCard, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
            <div style={{ ...styles.summaryNumber, color: dynamicStyles.textColor }}>{report?.total_candidates || 0}</div>
            <div style={{ ...styles.summaryLabel, color: dynamicStyles.textSecondary }}>Total Candidates</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryNumber, color: '#22c55e' }}>
              {strongHireCount}
            </div>
            <div style={styles.summaryLabel}>Strong Hire</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryNumber, color: '#3b82f6' }}>
              {hireCount}
            </div>
            <div style={styles.summaryLabel}>Hire</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryNumber, color: '#eab308' }}>
              {maybeCount}
            </div>
            <div style={styles.summaryLabel}>Maybe</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryNumber, color: '#9ca3af' }}>
              {unanalyzedCount}
            </div>
            <div style={styles.summaryLabel}>Pending</div>
          </div>
        </div>

        {/* Analyze All Button */}
        {unanalyzedCount > 0 && (
          <div style={styles.actionBar}>
            <button 
              onClick={analyzeAll}
              disabled={analyzingAll}
              style={{ ...styles.analyzeButton, background: primaryColor }}
            >
              {analyzingAll ? 'Analyzing...' : `Analyze ${unanalyzedCount} Pending Interviews`}
            </button>
          </div>
        )}

        {/* Candidates List */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Candidates (Ranked by Score)</h2>
          
          {candidates?.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No candidates have interviewed for this job yet.</p>
            </div>
          ) : (
            <div style={styles.candidatesList}>
              {candidates?.map((candidate, index) => (
                <div 
                  key={candidate.interview_id}
                  style={styles.candidateCard}
                  onClick={() => navigate(getPath(`/interviews/${candidate.interview_id}/report`))}
                >
                  <div style={styles.candidateRank}>#{index + 1}</div>
                  
                  <div style={styles.candidateInfo}>
                    <div style={styles.candidateName}>
                      {candidate.candidate_name || candidate.candidate_email || 'Unknown'}
                    </div>
                    <div style={styles.candidateMeta}>
                      {candidate.candidate_email}
                    </div>
                    {candidate.key_strengths?.length > 0 && (
                      <div style={styles.strengthsPreview}>
                        {candidate.key_strengths.slice(0, 2).map((s, i) => (
                          <span key={i} style={styles.strengthTag}>✓ {s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div style={styles.candidateScore}>
                    {candidate.overall_score !== null ? (
                      <>
                        <div 
                          style={{
                            ...styles.scoreCircle,
                            borderColor: getScoreColor(candidate.overall_score),
                            color: getScoreColor(candidate.overall_score)
                          }}
                        >
                          {candidate.overall_score.toFixed(1)}
                        </div>
                        <div 
                          style={{
                            ...styles.recommendationBadge,
                            ...getRecommendationStyle(candidate.recommendation)
                          }}
                        >
                          {candidate.recommendation || 'Pending'}
                        </div>
                      </>
                    ) : (
                      <div style={styles.pendingBadge}>
                        {candidate.has_transcript ? 'Awaiting Analysis' : 'No Transcript'}
                      </div>
                    )}
                  </div>
                  
                  <div style={styles.candidateActions}>
                    <span style={{ ...styles.viewLink, color: primaryColor }}>View Details →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#9ca3af'
  },
  error: {
    padding: 40,
    textAlign: 'center',
    color: '#ef4444'
  },
  header: {
    padding: '24px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 12,
    padding: 0
  },
  backButton: {
    marginTop: 16,
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer'
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 8px 0'
  },
  companyName: {
    fontSize: 14,
    color: '#9ca3af',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: 16,
    color: '#9ca3af',
    margin: 0
  },
  content: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '32px 40px'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 16,
    marginBottom: 32
  },
  summaryCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: 16,
    textAlign: 'center'
  },
  summaryNumber: {
    fontSize: 28,
    fontWeight: 700,
    color: '#fff'
  },
  summaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4
  },
  actionBar: {
    marginBottom: 32
  },
  analyzeButton: {
    padding: '12px 24px',
    background: '#3ECF8E',
    color: '#11181C',
    border: 'none',
    borderRadius: 6,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14
  },
  section: {
    marginBottom: 32
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 20
  },
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: '#6b7280',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 12
  },
  candidatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  candidateCard: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 150px 120px',
    alignItems: 'center',
    gap: 20,
    padding: 20,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'border-color 0.2s'
  },
  candidateRank: {
    fontSize: 18,
    fontWeight: 700,
    color: '#6b7280'
  },
  candidateInfo: {
    overflow: 'hidden'
  },
  candidateName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 4
  },
  candidateMeta: {
    fontSize: 13,
    color: '#6b7280'
  },
  strengthsPreview: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
    flexWrap: 'wrap'
  },
  strengthTag: {
    fontSize: 11,
    color: '#22c55e',
    background: 'rgba(34, 197, 94, 0.1)',
    padding: '2px 8px',
    borderRadius: 4
  },
  candidateScore: {
    textAlign: 'center'
  },
  scoreCircle: {
    width: 50,
    height: 50,
    borderRadius: '50%',
    border: '3px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 700,
    margin: '0 auto 8px'
  },
  recommendationBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    display: 'inline-block'
  },
  pendingBadge: {
    padding: '4px 12px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    fontSize: 12,
    color: '#9ca3af'
  },
  candidateActions: {
    textAlign: 'right'
  },
  viewLink: {
    fontSize: 14,
    fontWeight: 500
  }
};
